import { AlertCircle, CheckCircle2, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * StatusTab — Phase 9.5.
 *
 * Client-user-only tab that surfaces:
 *   1. Every REQUIRED field the tenant flagged as required (via Form
 *      Designer) that's currently empty on this booking.
 *   2. A "Driver TBA" outstanding row when driver_tba = true.
 *
 * Each row is editable inline (simple text-input fallback for anything
 * we don't know how to render specially), plus a "Jump to <tab>" button
 * so the user can hop to the full editor for that field.
 *
 * Gated by BookingFormShell: only mounted when the tenant has ≥ 1
 * lbs_email_cc_recipients row AND the current user role is a client
 * user (booker / client_admin / site_admin).
 */
export default function StatusTab({
  effectiveFields,          // merged registry+overrides array
  values,
  set,
  onJumpToTab,              // (tabKey) => void
}) {
  // Consider a field "missing" when it's required AND its value is empty.
  const missing = (effectiveFields || []).filter((f) => {
    if (!f.required) return false;
    const v = values[f.key];
    if (v === undefined || v === null) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
  });

  const driverTba = !!values.driver_tba;
  const noneOutstanding = missing.length === 0 && !driverTba;

  return (
    <div className="space-y-3" data-testid="status-tab">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold">Booking completeness</h3>
        {noneOutstanding ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-xs">
            <CheckCircle2 className="h-3 w-3" /> All required fields filled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-xs">
            <AlertCircle className="h-3 w-3" /> {missing.length + (driverTba ? 1 : 0)} outstanding
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Fields shown here are required by your organisation but not yet filled.
        Update inline, or jump to the full editor for that field.
      </p>

      {/* Driver TBA row */}
      {driverTba && (
        <div className="border border-amber-200 bg-amber-50/40 rounded p-3 flex items-center gap-3"
             data-testid="status-driver-tba-row">
          <UserX className="h-4 w-4 text-amber-700 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium text-amber-900">Driver — TBA</div>
            <div className="text-xs text-amber-700">
              Booking will remain flagged as outstanding and gate check-in
              is blocked until a driver is assigned.
            </div>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => onJumpToTab?.("driver")}
                  data-testid="status-jump-driver">
            Assign driver →
          </Button>
        </div>
      )}

      {/* Missing required fields */}
      {missing.length === 0 && !driverTba && (
        <div className="text-sm text-slate-500 italic p-3 bg-slate-50 border border-slate-200 rounded"
             data-testid="status-none-missing">
          Nothing outstanding. This booking is ready to submit.
        </div>
      )}

      {missing.map((f) => (
        <div key={f.key}
             className="border border-slate-200 bg-white rounded p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center"
             data-testid={`status-missing-${f.key}`}>
          <div className="sm:col-span-4">
            <div className="text-sm font-medium">{f.label}</div>
            <div className="text-[11px] text-slate-500">
              <code className="font-mono">{f.key}</code> · in {f.tab}
            </div>
          </div>
          <div className="sm:col-span-6">
            <Input value={values[f.key] || ""}
                   onChange={(e) => set(f.key, e.target.value)}
                   placeholder={`Enter ${f.label.toLowerCase()}…`}
                   data-testid={`status-input-${f.key}`}
                   className="h-9" />
          </div>
          <div className="sm:col-span-2 text-right">
            <Button size="sm" variant="outline"
                    onClick={() => onJumpToTab?.(f.tab)}
                    data-testid={`status-jump-${f.key}`}>
              Jump to {f.tab} →
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
